/**
 * PaymentModal — VEN-07: Registrar/Editar pago de cliente
 * Permite asignar el pago a una o más facturas pendientes del cliente.
 */
import { todayISO } from '../../../lib/utils'
import { useState, useEffect, useMemo } from 'react'
import DatePicker from '@/components/DatePicker'
import { NumericInput } from '@/components/NumericInput'
import { DataTable } from '@/components/ds/DataTable'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import {
  useCreateCustomerPayment,
  useCustomers,
  useCollections,
  type CollectionInvoice,
  type CustomerPayment,
} from '../hooks/useSales'
import { BankAccountSelect } from '@/modules/accounting/components/BankAccountSelect'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'

interface PaymentModalProps {
  preselectedInvoice?: CollectionInvoice | null
  payment?: CustomerPayment
  onClose: () => void
  onSave?: (data: Record<string, unknown>) => void
  isPending?: boolean
}

interface AllocationRow {
  invoice_id: string
  invoice_folio: string
  invoice_total: string
  invoice_outstanding: string
  amount: string
}

/**
 * Lo que se propone cobrar de una factura.
 *
 * Con detracción el cliente **no transfiere el total**: retiene el porcentaje y lo
 * deposita en la cuenta del Banco de la Nación. Proponer el saldo completo obliga
 * a corregir el monto a mano en cada cobro — que es justamente lo que Odoo hace
 * hacer y acá se evita.
 *
 * La detracción NO reduce la deuda: el saldo se cancela igual por el total, solo
 * que con dos cobros por vías distintas.
 */
function montoPropuesto(inv: CollectionInvoice): string {
  const saldo = parseFloat(inv.outstanding || '0')
  const neto = parseFloat(inv.detraction?.neto_a_cobrar || '0')
  if (!inv.detraction || !neto) return inv.outstanding
  // Si ya hubo abonos, el saldo puede ser menor que el neto.
  return (Math.min(saldo, neto)).toFixed(2)
}

// fmt se define dentro del componente para acceder al locale del tenant

export function PaymentModal({ preselectedInvoice, payment, onClose, onSave, isPending: externalPending }: PaymentModalProps) {
  const locale = useTenantLocale()
  const isEditMode = !!payment

  const fmt = (value: string | undefined) => {
    if (!value) return '—'
    const n = parseFloat(value)
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }

  const [customerId, setCustomerId] = useState(payment?.customer || (preselectedInvoice?.customer ?? ''))
  const [paymentDate, setPaymentDate] = useState(payment?.payment_date || todayISO())
  const [amount, setAmount] = useState(
    payment?.amount || (preselectedInvoice ? montoPropuesto(preselectedInvoice) : ''))
  const [paymentMethod, setPaymentMethod] = useState<'transfer' | 'check' | 'cash' | 'card' | 'other'>(payment?.payment_method || 'transfer')
  const [reference, setReference] = useState(payment?.reference || '')
  const [notes, setNotes] = useState(payment?.notes || '')
  const [excedenteEsAnticipo, setExcedenteEsAnticipo] = useState(false)
  // El monto total sigue a la suma de asignaciones HASTA que alguien lo escriba
  // a mano. Registrar un abono es el caso normal —bajar «a aplicar» y listo— y
  // antes obligaba a corregir también el monto de arriba, que venía pre-llenado
  // con el saldo completo de la factura: si no se bajaba, el cobro se rechazaba
  // sin que quedara claro qué campo estaba mal.
  const [montoEditadoAMano, setMontoEditadoAMano] = useState(false)
  const [allocations, setAllocations] = useState<AllocationRow[]>(payment?.allocations?.map(a => ({
    invoice_id: a.invoice ?? '',
    invoice_folio: a.invoice_folio ?? '',
    invoice_total: a.invoice_folio ?? '', // Usar folio como fallback
    invoice_outstanding: a.amount ?? '',
    amount: a.amount,
  })) || [])
  const [bankAccountId, setBankAccountId] = useState(payment?.bank_account || '')
  const [leavePending, setLeavePending] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const createPayment = useCreateCustomerPayment()

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // Si hay factura preseleccionada, pre-rellenar la asignación
  useEffect(() => {
    if (preselectedInvoice) {
      setAllocations([{
        invoice_id: preselectedInvoice.id,
        invoice_folio: preselectedInvoice.folio ?? preselectedInvoice.id.slice(0, 8),
        invoice_total: preselectedInvoice.total,
        invoice_outstanding: preselectedInvoice.outstanding,
        amount: montoPropuesto(preselectedInvoice),
      }])
    }
  }, [preselectedInvoice])

  // Cargar clientes para el selector
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const customerItems = useMemo<AutocompleteItem[]>(
    () => (customersData?.customers ?? []).map(c => ({
      id: c.id,
      label: c.tax_id ? `${c.legal_name} (${c.tax_id})` : c.legal_name,
    })),
    [customersData],
  )

  // Cargar facturas pendientes del cliente seleccionado
  const { data: collectionsData } = useCollections({
    customer_id: customerId || undefined,
    page_size: 100,
  })
  const pendingInvoices = collectionsData?.invoices.filter(inv =>
    inv.collection_status !== 'paid' &&
    !allocations.find(a => a.invoice_id === inv.id)
  ) ?? []

  const totalAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.amount || '0'), 0)
  const paymentTotal = parseFloat(amount || '0')
  const allocationError = totalAllocated > paymentTotal
  // Lo que el cliente pagó de más. Mientras no se declare como anticipo el
  // backend rechaza el cobro: un excedente sin declarar queda como remanente
  // invisible que infla el estado de cuenta sin reducir ninguna factura.
  const excedente = paymentTotal > totalAllocated ? paymentTotal - totalAllocated : 0
  const hayExcedente = !isEditMode && excedente > 0 && !!amount

  /**
   * Deja el monto total igual a la suma de asignaciones, salvo que el usuario
   * ya lo haya escrito. Recibir MÁS que lo asignado existe y se resuelve como
   * anticipo, pero es raro: lo frecuente es el abono parcial, así que el
   * automático acompaña ese caso y el otro queda como decisión explícita.
   */
  const sincronizarMonto = (filas: AllocationRow[]) => {
    if (montoEditadoAMano || isEditMode) return
    const suma = filas.reduce((acc, a) => acc + parseFloat(a.amount || '0'), 0)
    setAmount(suma > 0 ? String(suma) : '')
  }

  const aplicarFilas = (filas: AllocationRow[]) => {
    setAllocations(filas)
    sincronizarMonto(filas)
  }

  const addAllocation = (inv: CollectionInvoice) => {
    aplicarFilas([...allocations, {
      invoice_id: inv.id,
      invoice_folio: inv.folio ?? inv.id.slice(0, 8),
      invoice_total: inv.total,
      invoice_outstanding: inv.outstanding,
      amount: montoPropuesto(inv),
    }])
  }

  /*
    Las dos identifican la fila por su FACTURA y no por su posición en el array.
    No es cosmético: el `render` de una tabla recibe el índice de la fila ya
    ordenada, así que con la posición el día que alguien marque una columna
    `sortable` la edición escribiría el monto en otra factura, sin ningún error.

    ⚠️ El `invoice_id` no se repite, y no es un supuesto: `pendingInvoices`
    descarta las que ya están asignadas, que es la única vía por la que una fila
    entra acá.
  */
  const removeAllocation = (invoiceId: string) => {
    aplicarFilas(allocations.filter(a => a.invoice_id !== invoiceId))
  }

  const updateAllocationAmount = (invoiceId: string, value: string) => {
    aplicarFilas(allocations.map(a => (a.invoice_id === invoiceId ? { ...a, amount: value } : a)))
  }

  const handleSave = () => {
    if (!customerId || !amount || !paymentDate) return
    if (!isEditMode && allocationError) return
    if (hayExcedente && !excedenteEsAnticipo) return

    setApiError(null)
    const payload: Record<string, unknown> = {
      customer: customerId,
      payment_date: paymentDate,
      amount,
      payment_method: paymentMethod,
      reference,
      notes,
    }

    // Solo incluir allocations en modo creación
    if (!isEditMode) {
      payload.allocations = allocations.map(a => ({
        invoice: a.invoice_id,
        amount: a.amount,
      }))
      if (excedente > 0) payload.overpayment_as_credit = excedenteEsAnticipo
      // Auto-confirmación: por defecto el cobro queda Confirmado (ya está en banco).
      payload.confirm = !leavePending
    }
    if (bankAccountId) payload.bank_account = bankAccountId

    // Si hay onSave personalizado (modo edición), usarlo
    if (onSave) {
      onSave(payload)
      return
    }

    createPayment.mutate(payload as Parameters<typeof createPayment.mutate>[0], {
      onSuccess: () => onClose(),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al registrar el pago')
      },
    })
  }

  return (
    <Modal open onClose={onClose} title={isEditMode ? 'Editar Pago de Cliente' : 'Registrar Pago de Cliente'} size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={(externalPending || createPayment.isPending) || !customerId || !amount || !paymentDate || (!isEditMode && (allocationError || (hayExcedente && !excedenteEsAnticipo)))}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {(externalPending || createPayment.isPending) && <Loader2 size={14} className="animate-spin" />}
          {isEditMode ? 'Guardar Cambios' : 'Registrar Pago'}
        </button>
      </>}
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 space-y-4">
        {/* Cliente */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Cliente *</label>
            {(!!preselectedInvoice || isEditMode) ? (
              <div className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content-muted bg-glass-3">
                {customerItems.find(i => i.id === customerId)?.label ?? '—'}
              </div>
            ) : (
              <Autocomplete aria-label="Cliente"
                items={customerItems}
                value={customerId}
                displayValue={customerItems.find(i => i.id === customerId)?.label ?? ''}
                onChange={() => { /* selección vía onSelect */ }}
                onSelect={(item: AutocompleteItem) => { setCustomerId(item.id); setAllocations([]) }}
                placeholder="Buscar cliente…"
              />
            )}
          </div>
          <div>
            <label htmlFor="paymentmodal-fecha-de-pago" className="block text-xs font-medium text-content-muted mb-1">Fecha de Pago *</label>
            <DatePicker id="paymentmodal-fecha-de-pago" value={paymentDate} onChange={setPaymentDate} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cobro-monto-total" className="block text-xs font-medium text-content-muted mb-1">Monto Total *</label>
            <NumericInput
              id="cobro-monto-total"
              value={amount}
              onChange={v => { setMontoEditadoAMano(true); setAmount(v) }}
              locale={locale}
              decimals={0}
              placeholder="0"
              min={0}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="paymentmodal-metodo-de-pago" className="block text-xs font-medium text-content-muted mb-1">Método de Pago</label>
            <select id="paymentmodal-metodo-de-pago"
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value as 'transfer' | 'check' | 'cash' | 'card' | 'other')}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="transfer">Transferencia</option>
              <option value="check">Cheque</option>
              <option value="cash">Efectivo</option>
              <option value="card">Tarjeta</option>
              <option value="other">Otro</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">
            Cuenta Banco / Caja
          </label>
          <BankAccountSelect value={bankAccountId} onChange={setBankAccountId} />
          <p className="text-[10px] text-content-subtle mt-0.5">
            Cuenta contable que recibirá el pago al contabilizar (Activo o Pasivo). Busca por código o nombre. Si no seleccionas, se usa la cuenta por defecto configurada en Contabilidad.
          </p>
        </div>

        <div>
          <label htmlFor="paymentmodal-referencia-n-de-comprobante" className="block text-xs font-medium text-content-muted mb-1">Referencia / N° de comprobante</label>
          <input id="paymentmodal-referencia-n-de-comprobante"
            type="text"
            value={reference}
            onChange={e => setReference(e.target.value)}
            placeholder="Ej: TRF-00123"
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Asignación a facturas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-content-muted">Asignar a Facturas</label>
            <span className={`text-xs font-medium ${allocationError ? 'text-danger-content-soft' : 'text-content-subtle'}`}>
              Asignado: {fmt(String(totalAllocated))} / {fmt(amount)}
            </span>
          </div>

          {/* Sin este aviso, el monto propuesto (menor que el saldo) parece un
              error de cálculo. La detracción no perdona deuda: la cancela por
              otra vía. */}
          {!isEditMode && preselectedInvoice?.detraction && (
            <div className="mb-2 rounded-lg border border-warning-border-strong bg-warning-surface px-3 py-2 text-xs">
              <p className="font-medium text-warning-content-strong">
                Operación sujeta a detracción ({preselectedInvoice.detraction.porcentaje}% ·
                código {preselectedInvoice.detraction.codigo})
              </p>
              <p className="mt-1 text-warning-content-strong">
                De {fmt(preselectedInvoice.outstanding)}, el cliente transfiere{' '}
                <strong>{fmt(preselectedInvoice.detraction.neto_a_cobrar)}</strong> y deposita{' '}
                <strong>S/ {preselectedInvoice.detraction.monto_pen}</strong> en la cuenta del
                Banco de la Nación {preselectedInvoice.detraction.cuenta_banco_nacion}.
                La factura se cancela por el total, con dos cobros.
              </p>
            </div>
          )}

          {!isEditMode && (
            <>
              {allocations.length > 0 && (
                <div className="mb-2">
                  <DataTable<AllocationRow>
                    columns={[
                      { key: 'invoice_folio', header: 'Folio' },
                      { key: 'invoice_outstanding', header: 'Pendiente', numeric: true,
                        render: a => (
                          <span className="text-content-muted">{fmt(a.invoice_outstanding)}</span>
                        ) },
                      { key: 'amount', header: 'A aplicar', numeric: true, width: '32%',
                        render: a => (
                          <input
                            type="number"
                            value={a.amount}
                            onChange={e => updateAllocationAmount(a.invoice_id, e.target.value)}
                            min="0"
                            max={a.invoice_outstanding}
                            aria-label={`Monto a aplicar a la factura ${a.invoice_folio}`}
                            className="w-28 border border-divider rounded px-2 py-1 text-sm text-right focus:ring-1 focus:ring-primary"
                          />
                        ) },
                      { key: 'quitar', header: '', width: 40,
                        render: a => (
                          <div className="text-right">
                            <button type="button" onClick={() => removeAllocation(a.invoice_id)}
                              aria-label={`Quitar la factura ${a.invoice_folio} del cobro`}
                              className="p-1 rounded text-content-subtle hover:text-danger-solid hover:bg-danger-surface transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ) },
                    ]}
                    rows={allocations}
                    rowKey={a => a.invoice_id}
                    caption="Facturas a las que se aplica este cobro"
                  />
                </div>
              )}

              {allocationError && (
                <p className="text-xs text-danger-content-soft mb-2">
                  La suma de asignaciones supera el monto del pago.
                </p>
              )}

              {hayExcedente && (
                <div className="mb-3 rounded-lg border border-warning-border bg-warning-surface p-3">
                  <p className="text-xs text-warning-content-strong font-medium mb-1">
                    El cliente pagó {fmt(String(excedente))} de más
                  </p>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      id="excedente-es-anticipo"
                      type="checkbox"
                      className="mt-0.5 rounded border-divider text-primary focus:ring-2 focus:ring-primary"
                      checked={excedenteEsAnticipo}
                      onChange={e => setExcedenteEsAnticipo(e.target.checked)}
                    />
                    <span className="text-xs text-warning-content">
                      Dejar el excedente como anticipo del cliente. Queda como saldo a favor
                      y después se puede aplicar a otra factura o devolver.
                    </span>
                  </label>
                  {!excedenteEsAnticipo && (
                    <p className="text-xs text-warning-content-soft mt-2">
                      Si no corresponde, asignar el saldo restante a una factura o corregir
                      el monto.
                    </p>
                  )}
                </div>
              )}

              {/* Agregar factura */}
              {customerId && pendingInvoices.length > 0 && (
                <div className="flex gap-2">
                  <select aria-label="Seleccionar factura"
                    className="flex-1 border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary"
                    defaultValue=""
                    onChange={e => {
                      const inv = pendingInvoices.find(i => i.id === e.target.value)
                      if (inv) { addAllocation(inv); e.target.value = '' }
                    }}
                  >
                    <option value="" disabled>Seleccionar factura...</option>
                    {pendingInvoices.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.folio ? `Folio ${inv.folio}` : `ID ${inv.id.slice(0, 8)}`} — {fmt(inv.outstanding)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {customerId && pendingInvoices.length === 0 && allocations.length === 0 && (
                <p className="text-xs text-content-subtle">No hay facturas pendientes para este cliente.</p>
              )}
            </>
          )}
        </div>

        <div>
          <label htmlFor="paymentmodal-notas-internas" className="block text-xs font-medium text-content-muted mb-1">Notas internas</label>
          <textarea id="paymentmodal-notas-internas"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Observaciones opcionales..."
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm text-content focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        {!isEditMode && (
          <label className="flex items-start gap-2 text-xs text-content cursor-pointer bg-soft border border-divider rounded-lg px-3 py-2">
            <input type="checkbox" checked={leavePending} onChange={e => setLeavePending(e.target.checked)} className="mt-0.5 rounded border-divider-strong" />
            <span>
              <strong>Dejar pendiente de confirmación.</strong> Por defecto el cobro queda <strong>Confirmado</strong>
              {' '}y se genera el comprobante contable. Marca esto solo si aún no está confirmado en el banco.
            </span>
          </label>
        )}

        {apiError && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">
            {apiError}
          </div>
        )}
      </div>

      {/* Footer */}
    </Modal>
  )
}
